import { MiddlewareAPI, PayloadAction } from '@reduxjs/toolkit'
import { ActivityPartAxis, ActivityPathId } from '@/api/models/aggregate'
import { isLongRead } from '@/features/activity/helpers/isLongRead'
import { getParentAxis, hasAxis } from '@/hooks/useActivityParts/helpers/getParentAxis'
import { AppDispatch, RootState } from '@/store'
import { objectiveWorkflowApi } from '@/store/api/objectiveWorkflowApi'
import { setActiveActivityPartAxis } from '@/store/slices/aggregateSlice'
import { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'
import { addBlockingOperation, removeBlockingOperation } from '@/store/slices/loadingSlice'
import { findActivityPartByActivityPathId } from './helpers/findActivityPartByActivityPathId'

// fires on left menu, and other cases where we are setting current activity
export const aggregateSetActiveActivityPartAxisMiddleware =
  (store: MiddlewareAPI) => (next: (action: unknown) => unknown) => (action: unknown) => {
    const payloadAction = action as PayloadAction<ActivityPartAxis>

    if (payloadAction.type !== setActiveActivityPartAxis.type) {
      return next(payloadAction)
    }

    const dispatch = store.dispatch as AppDispatch
    const nextActivityPartAxis = payloadAction.payload

    const activitiesWithAggregates = (store.getState() as RootState).aggregate.activitiesWithAggregates
    const objectiveWorkflowId = (store.getState() as RootState).aggregate.current?.lastObjectiveWorkflow?.id

    if (!activitiesWithAggregates || !objectiveWorkflowId || !nextActivityPartAxis) {
      return next(payloadAction)
    }

    const nextActivityPart = findActivityPartByActivityPathId(activitiesWithAggregates, nextActivityPartAxis)

    const findParentActivityPart = (): Pick<
      FrontActivityPart<FrontActivityWithAggregate>,
      'activityPathId' | 'childActivity'
    > => {
      const parentActivityPartAxis = getParentAxis(nextActivityPartAxis)

      return hasAxis(parentActivityPartAxis)
        ? findActivityPartByActivityPathId(activitiesWithAggregates, parentActivityPartAxis)
        : { childActivity: activitiesWithAggregates, activityPathId: '' as ActivityPathId }
    }

    const parentActivityPart = findParentActivityPart()

    const getMethod = (): any | null => {
      if (!isLongRead(parentActivityPart.childActivity)) {
        return objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowAggregate.initiate({
          objectiveWorkflowId,
          activityPathId: nextActivityPart.activityPathId,
        })
      }

      const needToFetchKids = parentActivityPart.childActivity.children.some(
        (activityPart: FrontActivityPart<FrontActivityWithAggregate>) =>
          !activityPart.available || !activityPart.contentAvailable,
      )

      if (!needToFetchKids) {
        return null
      }

      return objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowChildren.initiate({
        objectiveWorkflowId,
        activityPathId: parentActivityPart.activityPathId,
      })
    }
    const method = getMethod()

    if (method) {
      dispatch(addBlockingOperation())

      dispatch(method).finally(() => {
        dispatch(removeBlockingOperation())
      })
    }

    return next(payloadAction)
  }
